import { Language } from "@commercial-publishing/shared-types";
import {
  MetricType,
  Network,
  PackageType,
  ProductId,
} from "@commercial-publishing/shared-types/social";
import type { MetricValue, ProductConfiguration } from "@/types/social.ts";

export function getAvailableMetrics(
  product: ProductConfiguration,
): MetricType[] {
  const metrics = new Set<MetricType>();
  Object.values(product.packages).forEach((pkg) => {
    if (pkg) {
      Object.keys(pkg).forEach((metric) => {
        metrics.add(metric as MetricType);
      });
    }
  });
  return Array.from(metrics);
}

export function getAvailablePackages(
  product: ProductConfiguration,
): PackageType[] {
  return Object.keys(product.packages).filter(
    (pkg) => product.packages[pkg as PackageType] !== undefined,
  ) as PackageType[];
}

export function isLanguageSpecific(
  value: MetricValue,
  // TODO: use enum
): value is { [Language.German]: number; [Language.French]: number } {
  return typeof value === "object" && value !== null;
}

export function getTargetValue(
  product: ProductConfiguration,
  packageType: PackageType,
  metric: MetricType,
  language?: Language,
): number | null {
  const packageData = product.packages[packageType];

  if (!packageData) return null;

  const metricValue = packageData[metric];

  if (!metricValue) return null;

  if (isLanguageSpecific(metricValue)) {
    if (!language || language === Language.Italian) return null;
    return metricValue[language];
  }

  return metricValue as number;
}

export function getMetricDisplayName(metric: MetricType): string {
  const displayNames = {
    [MetricType.Impressions]: "Impressions",
    [MetricType.Clicks]: "Clicks",
    [MetricType.ThruPlays]: "ThruPlays",
    [MetricType.Views]: "Views",
    [MetricType.StoryViews]: "Story Views",
    [MetricType.VideoViews]: "Video Views",
    [MetricType.LinkClicks]: "Link Clicks",
    [MetricType.CrossMedia]: "Cross Media",
    [MetricType.Shares]: "Shares",
  };
  return displayNames[metric];
}

export function getPackageDisplayName(packageType: PackageType): string {
  const displayNames = {
    [PackageType.Basic]: "Basic",
    [PackageType.Pro]: "Pro",
    [PackageType.Mini]: "Mini",
    [PackageType.Midi]: "Midi",
  };
  return displayNames[packageType];
}

export function getPlatformDisplayName(platform: Network): string {
  const displayNames = {
    [Network.Instagram]: "Instagram",
    [Network.TikTok]: "TikTok",
    [Network.Facebook]: "Facebook",
    [Network.Snapchat]: "Snapchat",
    [Network.WhatsApp]: "WhatsApp",
  };
  return displayNames[platform];
}

export function getProduct(productId: ProductId): ProductConfiguration {
  return PRODUCTS[productId];
}

export const PRODUCTS = Object.freeze({
  [ProductId.SocialBrandVideo]: {
    name: "Social Brand Video",
    description:
      "Available in German & French, distributed on Instagram (Meta) & TikTok",
    platforms: [Network.Instagram, Network.TikTok],
    maxNumberOfLinks: 2,
    kpiMetric: MetricType.Impressions,
    hasLanguageTargeting: false,
    packages: {
      [PackageType.Basic]: {
        [MetricType.Impressions]: 500000,
        [MetricType.LinkClicks]: 1800,
        [MetricType.ThruPlays]: 70000,
      },
      [PackageType.Pro]: {
        [MetricType.Impressions]: 1000000,
        [MetricType.LinkClicks]: 3600,
        [MetricType.ThruPlays]: 130000,
      },
    },
  },
  [ProductId.SocialLinks]: {
    name: "Social Links",
    description:
      "Legacy social media posts with Mini/Midi/Pro targeting across multiple platforms",
    platforms: [Network.Facebook, Network.Instagram, Network.TikTok],
    maxNumberOfLinks: 1,
    kpiMetric: MetricType.Impressions,
    hasLanguageTargeting: false,
    packages: {
      [PackageType.Mini]: {
        [MetricType.Impressions]: 250000,
        [MetricType.VideoViews]: 30000,
        [MetricType.LinkClicks]: 1000,
        [MetricType.CrossMedia]: 70000,
      },
      [PackageType.Midi]: {
        [MetricType.Impressions]: 470000,
        [MetricType.VideoViews]: 60000,
        [MetricType.LinkClicks]: 1750,
        [MetricType.CrossMedia]: 160000,
      },
      [PackageType.Pro]: {
        [MetricType.Impressions]: 840000,
        [MetricType.VideoViews]: 100000,
        [MetricType.LinkClicks]: 2500,
        [MetricType.CrossMedia]: 280000,
      },
    },
  },
  [ProductId.SocialBrandStories]: {
    name: "Social Brand Stories",
    description: "Distributed only via Instagram (Stories)",
    platforms: [Network.Instagram],
    maxNumberOfLinks: 1,
    kpiMetric: MetricType.StoryViews,
    hasLanguageTargeting: true,
    packages: {
      [PackageType.Basic]: {
        [MetricType.Views]: {
          [Language.German]: 110000,
          [Language.French]: 80000,
        },
      },
      [PackageType.Pro]: {
        [MetricType.Views]: {
          [Language.German]: 220000,
          [Language.French]: 170000,
        },
      },
    },
  },
  [ProductId.SocialCrossmediaTeaser]: {
    name: "Social Crossmedia Teaser",
    description:
      "Distributed via Facebook Link, plus organic Instagram Story + Snapchat Story + WhatsApp Boost",
    platforms: [
      Network.Facebook,
      Network.Instagram,
      Network.Snapchat,
      Network.WhatsApp,
    ],
    maxNumberOfLinks: 20,
    kpiMetric: MetricType.Impressions,
    hasLanguageTargeting: true,
    packages: {
      [PackageType.Basic]: {
        [MetricType.Impressions]: {
          [Language.German]: 100000,
          [Language.French]: 70000,
        },
      },
      [PackageType.Pro]: {
        [MetricType.Impressions]: {
          [Language.German]: 200000,
          [Language.French]: 160000,
        },
      },
    },
  },
});
